import html2pdf from 'html2pdf.js';

function padOsNumber(id, size = 6) {
  const s = String(id ?? '');
  return s.padStart(size, '0');
}

function bufferToDataUrl(bufferLike, mime = 'image/png') {
  if (!bufferLike) return null;
  if (typeof bufferLike === 'string') return bufferLike;
  if (bufferLike?.type === 'Buffer' && Array.isArray(bufferLike.data)) {
    // Avoid call stack limits with large arrays
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bufferLike.data.length; i += chunkSize) {
      const chunk = bufferLike.data.slice(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    const base64 = btoa(binary);
    return `data:${mime};base64,${base64}`;
  }
  return null;
}

function formatDatePtBR(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('pt-BR');
  } catch {
    return String(dateStr);
  }
}

function formatDateTimePtBR(dateTime) {
  if (!dateTime) return '-';
  try {
    return new Date(dateTime).toLocaleString('pt-BR');
  } catch {
    return String(dateTime);
  }
}

function formatMoneyBR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function buildAddress({ street, number, complement, neighborhood, city, state, zip_code }) {
  const line1 = [street, number].filter(Boolean).join(' ');
  const line2 = [neighborhood, city && state ? `${city}/${state}` : (city || state)].filter(Boolean).join(' - ');
  const line3 = zip_code ? `CEP: ${zip_code}` : '';
  const comp = complement ? ` ${complement}` : '';
  const addr1 = (line1 ? `${line1}${comp}` : '').trim();
  return [addr1, line2, line3].filter(Boolean).join('<br/>');
}

function normalizeTarget(order, target) {
  const targetType = order?.target_type;
  if (!target) {
    return {
      name: order?.target_name || (order?.id ? `OS #${order.id}` : '—'),
      documentLabel: 'Documento',
      documentValue: '—',
      phone: '—',
      mobile: '—',
      addressHtml: '—',
    };
  }

  const name =
    target.trade_name ||
    target.nickname ||
    target.corporate_name ||
    target.name ||
    order?.target_name ||
    '—';

  let documentValue =
    target.document ||
    target.cnpj ||
    target.cpf ||
    target.owner_document ||
    null;

  const documentLabel =
    targetType === 'employee'
      ? 'CPF'
      : 'CPF/CNPJ';

  const phone =
    target.phone ||
    target.responsible_phone ||
    target.owner_phone ||
    target.secondary_phone ||
    '—';

  const mobile =
    target.mobile ||
    '—';

  const addr =
    target.address_street || target.street
      ? buildAddress({
          street: target.address_street || target.street,
          number: target.address_number || target.number,
          complement: target.address_complement || target.complement,
          neighborhood: target.address_district || target.neighborhood,
          city: target.address_city || target.city,
          state: target.address_state || target.state,
          zip_code: target.address_zip_code || target.zip_code,
        })
      : '';

  const addressHtml = addr || '—';

  return {
    name,
    documentLabel,
    documentValue: documentValue || '—',
    phone,
    mobile,
    addressHtml,
  };
}

export async function generateServiceOrderDocPdf({ company, order, target }) {
  const osNumber = padOsNumber(order?.id);
  const companyName = company?.trade_name || company?.corporate_name || 'Empresa';
  const companyCnpj = company?.cnpj ? `CNPJ: ${company.cnpj}` : '—';
  const companyPhone = company?.owner_phone || '—';
  const companyEmail = company?.owner_email || '—';
  const companyAddress = company
    ? [
        [company?.address_street, company?.address_number].filter(Boolean).join(', '),
        company?.address_complement,
        company?.address_district,
        [company?.address_city, company?.address_state].filter(Boolean).join('/'),
        company?.address_zip_code,
      ]
        .filter(Boolean)
        .join(' - ')
    : '—';

  const logoUrl = bufferToDataUrl(company?.logo) || null;

  const normalizedTarget = normalizeTarget(order, target);
  const targetName = normalizedTarget.name;
  const targetDoc = normalizedTarget.documentValue;
  const targetDocLabel = normalizedTarget.documentLabel;
  const targetPhone = normalizedTarget.phone;
  const targetMobile = normalizedTarget.mobile;
  const targetAddress = normalizedTarget.addressHtml;

  const services = Array.isArray(order?.services) ? order.services : [];
  const totalValue = Number(order?.value);
  const hasTotalValue = Number.isFinite(totalValue);
  const singleServiceWithValue = services.length === 1 && hasTotalValue;

  const servicesRowsHtml = services.length
    ? services
        .map((s) => {
          const code = String(s.id ?? '').padStart(5, '0');
          const desc = (s.name || '-').toString();
          const unit = singleServiceWithValue ? formatMoneyBR(totalValue) : '—';
          const rowTotal = singleServiceWithValue ? formatMoneyBR(totalValue) : '—';
          return `
            <tr>
              <td class="cell mono">${code}</td>
              <td class="cell">${desc}</td>
              <td class="cell right">1,00</td>
              <td class="cell right">${unit}</td>
              <td class="cell right">${rowTotal}</td>
            </tr>
          `;
        })
        .join('')
    : `
        <tr>
          <td class="cell mono">-</td>
          <td class="cell">${order?.title || 'Serviço'}</td>
          <td class="cell right">1,00</td>
          <td class="cell right">${hasTotalValue ? formatMoneyBR(totalValue) : '—'}</td>
          <td class="cell right">${hasTotalValue ? formatMoneyBR(totalValue) : '—'}</td>
        </tr>
      `;

  const subtotal = hasTotalValue ? totalValue : null;
  const desconto = 0;
  const total = hasTotalValue ? totalValue - desconto : null;

  const dtSolicitacao = order?.createdAt ? formatDateTimePtBR(order.createdAt) : '—';
  const previsaoEntrega = order?.next_execution_date ? formatDatePtBR(order.next_execution_date) : '—';
  const status = order?.status || '—';
  const solicitante = targetName || '—';
  const responsavelOS = company?.owner_name || '—';

  const html = `
    <div class="os-doc">
      <style>
        /* IMPORTANTE: tudo escopado em .os-doc para não vazar CSS na página */
        .os-doc { font-family: Arial, sans-serif; color: #111; font-size: 11px; }
        .os-doc .row { display: flex; justify-content: space-between; gap: 16px; }
        .os-doc .top-left { display: flex; gap: 12px; align-items: flex-start; }
        .os-doc .logo { width: 86px; height: 52px; object-fit: contain; }
        .os-doc .company { line-height: 1.2; }
        .os-doc .company .name { font-weight: 700; font-size: 12px; text-transform: uppercase; }
        .os-doc .company .meta { color: #333; }
        .os-doc .top-right { text-align: right; }
        .os-doc .doc-title { font-weight: 800; font-size: 18px; letter-spacing: 0.5px; }
        .os-doc .doc-no { font-weight: 800; font-size: 18px; margin-top: 2px; }
        .os-doc .hr { border-top: 2px solid #444; margin: 10px 0; }
        .os-doc .section-title { font-weight: 700; margin: 8px 0 6px; }
        .os-doc .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
        .os-doc .field { border-bottom: 1px solid #bbb; padding-bottom: 4px; }
        .os-doc .label { font-weight: 700; color: #222; font-size: 10px; }
        .os-doc .value { margin-top: 2px; }
        .os-doc table { width: 100%; border-collapse: collapse; }
        .os-doc th { background: #f2f2f2; border: 1px solid #666; padding: 6px 6px; font-weight: 700; font-size: 10px; text-align: left; }
        .os-doc td.cell { border: 1px solid #666; padding: 6px 6px; vertical-align: top; }
        .os-doc .right { text-align: right; }
        .os-doc .mono { font-family: 'Courier New', monospace; }
        .os-doc .footer-boxes { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; margin-top: 10px; }
        .os-doc .box { border: 1px solid #666; padding: 6px; text-align: center; }
        .os-doc .box .b-label { font-weight: 700; font-size: 10px; color: #222; }
        .os-doc .box .b-value { margin-top: 4px; font-weight: 700; }
        .os-doc .sign { margin-top: 22px; text-align: center; }
        /* Espaço maior para assinatura manual */
        .os-doc .sign-line { margin-top: 70px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
        .os-doc .sig { border-top: 1px solid #333; padding-top: 8px; font-size: 10px; min-height: 38px; }
      </style>

      <div class="row">
        <div class="top-left">
          ${logoUrl ? `<img class="logo" src="${logoUrl}" alt="Logo" />` : `<div class="logo"></div>`}
          <div class="company">
            <div class="name">${companyName}</div>
            <div class="meta">${companyCnpj !== '—' ? companyCnpj : ''}</div>
            <div class="meta">${companyAddress}</div>
            <div class="meta">${companyPhone !== '—' ? companyPhone : ''}${companyEmail !== '—' ? ` | ${companyEmail}` : ''}</div>
          </div>
        </div>
        <div class="top-right">
          <div class="doc-title">ORDEM DE</div>
          <div class="doc-title">SERVIÇO</div>
          <div class="doc-no">${osNumber}</div>
          <div style="margin-top:10px; font-size:10px;">
            <div><span class="label">Dt. Solicitação</span> ${dtSolicitacao}</div>
          </div>
        </div>
      </div>

      <div class="hr"></div>

      <div class="section-title">Dados do Cliente</div>
      <div class="grid">
        <div class="field">
          <div class="label">Cliente</div>
          <div class="value"><b>${targetName}</b></div>
        </div>
        <div class="field">
          <div class="label">${targetDocLabel}</div>
          <div class="value">${targetDoc || '—'}</div>
        </div>
        <div class="field">
          <div class="label">Contato</div>
          <div class="value">${[targetPhone !== '—' ? `Tel: ${targetPhone}` : null, targetMobile !== '—' ? `Cel: ${targetMobile}` : null].filter(Boolean).join(' | ') || '—'}</div>
        </div>
        <div class="field" style="grid-column: 1 / -1;">
          <div class="label">Endereço</div>
          <div class="value">${targetAddress || '—'}</div>
        </div>
      </div>

      <div class="section-title" style="margin-top:10px;">Informações da OS</div>
      <div class="grid">
        <div class="field">
          <div class="label">Situação</div>
          <div class="value">${status}</div>
        </div>
        <div class="field">
          <div class="label">Solicitante</div>
          <div class="value">${solicitante}</div>
        </div>
        <div class="field">
          <div class="label">Previsão Entrega</div>
          <div class="value">${previsaoEntrega}</div>
        </div>
        <div class="field">
          <div class="label">Título</div>
          <div class="value">${order?.title || '—'}</div>
        </div>
        <div class="field">
          <div class="label">Tipo de Serviço</div>
          <div class="value">${order?.service_type || '—'}</div>
        </div>
        <div class="field">
          <div class="label">Solicitação</div>
          <div class="value">${order?.request_type || '—'}</div>
        </div>
        <div class="field">
          <div class="label">Responsável OS</div>
          <div class="value">${responsavelOS}</div>
        </div>
        <div class="field" style="grid-column: 1 / -1;">
          <div class="label">Observação</div>
          <div class="value">${order?.observation ? String(order.observation).replaceAll('\n', '<br/>') : '—'}</div>
        </div>
      </div>

      <div class="section-title" style="margin-top:10px;">Serviços</div>
      <table>
        <thead>
          <tr>
            <th style="width: 12%;">Código</th>
            <th>Descrição</th>
            <th style="width: 12%;" class="right">Qtde</th>
            <th style="width: 18%;" class="right">Vlr. Unitário</th>
            <th style="width: 18%;" class="right">Vlr. Total</th>
          </tr>
        </thead>
        <tbody>
          ${servicesRowsHtml}
        </tbody>
      </table>

      <div class="footer-boxes">
        <div class="box">
          <div class="b-label">Subtotal</div>
          <div class="b-value">${subtotal === null ? '—' : formatMoneyBR(subtotal)}</div>
        </div>
        <div class="box">
          <div class="b-label">Produtos</div>
          <div class="b-value">—</div>
        </div>
        <div class="box">
          <div class="b-label">Serviços</div>
          <div class="b-value">${subtotal === null ? '—' : formatMoneyBR(subtotal)}</div>
        </div>
        <div class="box">
          <div class="b-label">Total Desconto</div>
          <div class="b-value">${formatMoneyBR(desconto)}</div>
        </div>
        <div class="box">
          <div class="b-label">% Desc.</div>
          <div class="b-value">${subtotal === null || subtotal === 0 ? '—' : `${((desconto / subtotal) * 100).toFixed(2).replace('.', ',')}%`}</div>
        </div>
        <div class="box">
          <div class="b-label">TOTAL</div>
          <div class="b-value">${total === null ? '—' : formatMoneyBR(total)}</div>
        </div>
      </div>

      <div class="sign">
        <div style="margin-top:14px;">Autorizo a execução dos serviços acima mencionados.</div>
        <div style="margin-top:6px;">${[company?.address_city, company?.address_state].filter(Boolean).join('/') || '—'}, ${dtSolicitacao}</div>
        <div class="sign-line">
          <div class="sig">
            ${(companyName || '').toUpperCase()}<br/>
            ${companyCnpj !== '—' ? companyCnpj : ''}
          </div>
          <div class="sig">
            ${(targetName || '').toUpperCase()}<br/>
            ${targetDoc && targetDoc !== '—' ? `${targetDocLabel}: ${targetDoc}` : ''}
          </div>
        </div>
      </div>
    </div>
  `;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const element = container.firstElementChild;
    await html2pdf()
      .set({
        margin: 10,
        filename: `OS_${osNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(element)
      .save();
  } finally {
    document.body.removeChild(container);
  }
}

